import { Injectable } from '@angular/core';
import { LanguageService } from './language.service';

export type SudokuGrid = (number | null)[][];

@Injectable({
    providedIn: 'root'
})
export class SudokuService {

    constructor(private langService: LanguageService) { }

    generatePuzzle(size: 4 | 6, difficulty: 'easy' | 'medium' | 'hard' = 'medium'): { puzzle: SudokuGrid, solution: number[][] } {
        const grid = this.createEmptyGrid(size);
        this.fillGrid(grid, size);
        const solution = grid.map(row => [...row]) as number[][];

        // Remove numbers to create the puzzle
        const puzzle = grid.map(row => [...row]) as SudokuGrid;
        const cellsToRemove = this.getCellsToRemove(size, difficulty);
        this.removeCells(puzzle, cellsToRemove, size);

        return { puzzle, solution };
    }

    private createEmptyGrid(size: number): number[][] {
        return Array.from({ length: size }, () => Array(size).fill(0));
    }

    private fillGrid(grid: number[][], size: number): boolean {
        for (let row = 0; row < size; row++) {
            for (let col = 0; col < size; col++) {
                if (grid[row][col] === 0) {
                    const numbers = this.shuffle(Array.from({ length: size }, (_, i) => i + 1));
                    for (const num of numbers) {
                        if (this.isValid(grid, row, col, num, size)) {
                            grid[row][col] = num;
                            if (this.fillGrid(grid, size)) return true;
                            grid[row][col] = 0;
                        }
                    }
                    return false;
                }
            }
        }
        return true;
    }

    isValid(grid: (number | null)[][], row: number, col: number, num: number, size: number): boolean {
        // Check row
        for (let x = 0; x < size; x++) if (grid[row][x] === num) return false;
        // Check column
        for (let x = 0; x < size; x++) if (grid[x][col] === num) return false;
        // Check region
        const boxWidth = size === 4 ? 2 : 3;
        const boxHeight = 2;
        const startRow = Math.floor(row / boxHeight) * boxHeight;
        const startCol = Math.floor(col / boxWidth) * boxWidth;
        for (let i = 0; i < boxHeight; i++) {
            for (let j = 0; j < boxWidth; j++) {
                if (grid[startRow + i][startCol + j] === num) return false;
            }
        }
        return true;
    }

    private shuffle(array: number[]): number[] {
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    }

    private getCellsToRemove(size: number, difficulty: string): number {
        const total = size * size;
        let percentClues: number;

        switch (difficulty) {
            case 'easy': percentClues = 0.50; break;
            case 'medium': percentClues = 0.37; break;
            case 'hard': percentClues = 0.20; break;
            default: percentClues = 0.37;
        }

        const clueCount = Math.max(size, Math.floor(total * percentClues));
        return total - clueCount;
    }

    private removeCells(grid: SudokuGrid, count: number, size: number) {
        let removed = 0;
        while (removed < count) {
            const row = Math.floor(Math.random() * size);
            const col = Math.floor(Math.random() * size);
            if (grid[row][col] !== null) {
                grid[row][col] = null;
                removed++;
            }
        }
    }

    getHint(grid: SudokuGrid, solution: number[][], size: number): { row: number, col: number, value: number, explanation: string } | null {
        const emptyCells: { row: number, col: number }[] = [];
        for (let r = 0; r < size; r++) {
            for (let c = 0; c < size; c++) {
                if (grid[r][c] === null) emptyCells.push({ row: r, col: c });
            }
        }

        if (emptyCells.length === 0) return null;

        // Pick a random empty cell
        const cell = emptyCells[Math.floor(Math.random() * emptyCells.length)];
        const value = solution[cell.row][cell.col];

        return {
            row: cell.row,
            col: cell.col,
            value: value,
            explanation: this.langService.translate('sudoku_hint_explanation', { value: value })
        };
    }

    validateGrid(grid: SudokuGrid, size: number): { valid: boolean, errors: { row: number, col: number }[] } {
        const errors: Set<string> = new Set();
        const boxWidth = size === 4 ? 2 : 3;
        const boxHeight = 2;

        // 1. Check rows
        for (let r = 0; r < size; r++) {
            const counts = new Map<number, number[]>();
            for (let c = 0; c < size; c++) {
                const val = grid[r][c];
                if (val !== null) {
                    if (!counts.has(val)) counts.set(val, []);
                    counts.get(val)!.push(c);
                }
            }
            counts.forEach((cols, val) => {
                if (cols.length > 1) {
                    cols.forEach(c => errors.add(`${r},${c}`));
                }
            });
        }

        // 2. Check columns
        for (let c = 0; c < size; c++) {
            const counts = new Map<number, number[]>();
            for (let r = 0; r < size; r++) {
                const val = grid[r][c];
                if (val !== null) {
                    if (!counts.has(val)) counts.set(val, []);
                    counts.get(val)!.push(r);
                }
            }
            counts.forEach((rows, val) => {
                if (rows.length > 1) {
                    rows.forEach(r => errors.add(`${r},${c}`));
                }
            });
        }

        // 3. Check regions
        for (let boxR = 0; boxR < size; boxR += boxHeight) {
            for (let boxC = 0; boxC < size; boxC += boxWidth) {
                const counts = new Map<number, { r: number, c: number }[]>();
                for (let i = 0; i < boxHeight; i++) {
                    for (let j = 0; j < boxWidth; j++) {
                        const r = boxR + i;
                        const c = boxC + j;
                        const val = grid[r][c];
                        if (val !== null) {
                            if (!counts.has(val)) counts.set(val, []);
                            counts.get(val)!.push({ r, c });
                        }
                    }
                }
                counts.forEach((cells, val) => {
                    if (cells.length > 1) {
                        cells.forEach(cell => errors.add(`${cell.r},${cell.c}`));
                    }
                });
            }
        }

        const errorList = Array.from(errors).map(s => {
            const [row, col] = s.split(',').map(Number);
            return { row, col };
        });

        // Check if full
        let full = true;
        for (let r = 0; r < size; r++) {
            for (let c = 0; c < size; c++) {
                if (grid[r][c] === null) full = false;
            }
        }

        return {
            valid: full && errorList.length === 0,
            errors: errorList
        };
    }
}
